import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import '../styles/dashboard.css';

interface ProtectedRouteProps {
    children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
    const { user, loading } = useAuth();
    const location = useLocation();

    if (loading) {
        // Show dashboard-like skeleton instead of bare spinner
        const isDashboard = location.pathname === '/dashboard';

        if (isDashboard) {
            return (
                <div className="dashboard-layout">
                    {/* Skeleton Sidebar */}
                    <aside className="dashboard-sidebar">
                        <div className="sidebar-header">
                            <div className="sidebar-logo">Ezyprep</div>
                        </div>
                        <div style={{ padding: '0 1rem' }}>
                            <div className="skeleton-line" style={{ height: '2.2rem', borderRadius: '10px', marginBottom: '1.5rem' }}></div>
                            <div className="skeleton-line" style={{ width: '40%', height: '0.6rem', marginBottom: '0.75rem' }}></div>
                            <div className="skeleton-line" style={{ height: '2.2rem', borderRadius: '10px', marginBottom: '4px' }}></div>
                            <div className="skeleton-line" style={{ height: '2.2rem', borderRadius: '10px', marginBottom: '4px' }}></div>
                            <div className="skeleton-line" style={{ height: '2.2rem', borderRadius: '10px', marginBottom: '1.5rem' }}></div>
                            <div className="skeleton-line" style={{ width: '40%', height: '0.6rem', marginBottom: '0.75rem' }}></div>
                            <div className="skeleton-line" style={{ height: '2.2rem', borderRadius: '10px', marginBottom: '4px' }}></div>
                            <div className="skeleton-line" style={{ height: '2.2rem', borderRadius: '10px', marginBottom: '4px' }}></div>
                            <div className="skeleton-line" style={{ height: '2.2rem', borderRadius: '10px' }}></div>
                        </div>
                    </aside>

                    {/* Skeleton Main Content */}
                    <main className="dashboard-main">
                        {/* Welcome area skeleton */}
                        <div style={{ marginBottom: '2rem' }}>
                            <div className="skeleton-line" style={{ width: '45%', height: '1.6rem', marginBottom: '0.6rem' }}></div>
                            <div className="skeleton-line" style={{ width: '30%', height: '0.85rem' }}></div>
                        </div>

                        {/* Section title skeleton */}
                        <div className="skeleton-line" style={{ width: '15%', height: '1rem', marginBottom: '1.5rem' }}></div>

                        {/* Project cards skeleton */}
                        <div className="projects-grid">
                            {[1, 2, 3, 4].map(i => (
                                <div key={i} className="project-card skeleton-card">
                                    <div className="skeleton-thumbnail"></div>
                                    <div className="project-card-content">
                                        <div className="skeleton-line skeleton-icon-block"></div>
                                        <div className="skeleton-line" style={{ width: '70%', height: '1.2rem' }}></div>
                                        <div className="skeleton-line" style={{ width: '50%', height: '0.8rem', marginTop: '0.5rem' }}></div>
                                        <div className="skeleton-line" style={{ width: '90%', height: '0.7rem', marginTop: '1rem' }}></div>
                                        <div className="skeleton-line" style={{ width: '60%', height: '0.7rem', marginTop: '0.4rem' }}></div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </main>
                </div>
            );
        }

        // Generic loading for non-dashboard routes
        return (
            <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100vh',
                background: '#0a0a0f',
                color: 'rgba(255,255,255,0.6)',
                fontFamily: 'Inter, sans-serif',
            }}>
                <div style={{ textAlign: 'center' }}>
                    <div style={{
                        width: 40,
                        height: 40,
                        border: '3px solid rgba(255,255,255,0.1)',
                        borderTopColor: '#9c27b0',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite',
                        margin: '0 auto 16px',
                    }} />
                    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                    <p>Loading...</p>
                </div>
            </div>
        );
    }

    if (!user) {
        return <Navigate to="/signin" replace />;
    }

    return <>{children}</>;
};

export default ProtectedRoute;
